import React, { useState, useEffect } from "react";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Card, Input, Typography, message } from "antd";
import FiltersWrapper from "../../Components/FiltersWrapper/FiltersWrapper";
import "./UtmParameters.scss";

const { Text } = Typography;

const UtmParameters = () => {
  const [formData, setFormData] = useState({
    websiteUrl: "",
    campaignId: "",
    campaignSource: "",
    campaignMedium: "",
    campaignName: "",
    campaignTerm: "",
    campaignContent: "",
  });

  const [generatedUrl, setGeneratedUrl] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isRequiredFieldsFilled = () => {
    return (
      formData.websiteUrl &&
      formData.campaignSource &&
      formData.campaignMedium &&
      (formData.campaignName || formData.campaignId)
    );
  };

  useEffect(() => {
    if (isRequiredFieldsFilled()) {
      generateUrl();
    } else {
      setGeneratedUrl("");
    }
  }, [formData]);

  const generateUrl = () => {
    try {
      const baseUrl = formData.websiteUrl?.trim();

      if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
        message.error("Please enter a valid website URL");
        return;
        // baseUrl = `https://${baseUrl}`;
      }

      const url = new URL(baseUrl);

      if (formData.campaignSource?.trim()) {
        url.searchParams.set("utm_source", formData.campaignSource.trim());
      } else {
        message.error("Please enter a valid campaign source");
        return;
      }
      if (formData.campaignMedium?.trim()) {
        url.searchParams.set("utm_medium", formData.campaignMedium.trim());
      } else {
        message.error("Please enter a valid campaign medium");
        return;
      }
      if (formData.campaignName?.trim()) {
        url.searchParams.set("utm_campaign", formData.campaignName.trim());
      }
      if (formData.campaignId?.trim()) {
        url.searchParams.set("utm_id", formData.campaignId.trim());
      }
      if (formData.campaignTerm?.trim()) {
        url.searchParams.set("utm_term", formData.campaignTerm.trim());
      }
      if (formData.campaignContent?.trim()) {
        url.searchParams.set("utm_content", formData.campaignContent.trim());
      }

      setGeneratedUrl(url.toString());
    } catch (error) {
      setGeneratedUrl("");
    }
  };

  const handleCopyUrl = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      message.success("URL copied to clipboard!");
    }
  };

  const renderInputField = (
    label,
    field,
    placeholder,
    helperText,
    required = false,
    boldWords = []
  ) => {
    const renderHelperText = () => {
      if (!boldWords.length) {
        return helperText;
      }

      let text = helperText;
      boldWords.forEach((word) => {
        text = text.replace(word, `<strong>${word}</strong>`);
      });

      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    };

    return (
      <div className="utm-parameters__field">
        <div className="utm-parameters__field-label">
          {label}
          {required && <span className="utm-parameters__required">*</span>}
        </div>
        <Input
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          size="large"
          className="utm-parameters__input"
        />
        <div className="utm-parameters__helper-text">{renderHelperText()}</div>
      </div>
    );
  };

  return (
    <FiltersWrapper>
      <div className="utm-parameters">
        <div className="utm-parameters__header">
          <Typography.Title level={4} className="utm-parameters__title">
            Enter the website URL and campaign information
          </Typography.Title>
          <Text className="utm-parameters__subtitle">
            Fill out all fields marked with an asterisk (*), and the campaign
            URL will be generated for you.
          </Text>
        </div>

        <Card className="utm-parameters__card">
          {renderInputField(
            "website URL",
            "websiteUrl",
            "Enter website URL",
            "The full website URL (e.g. https://www.example.com)",
            true,
            ["https://www.example.com"]
          )}

          {renderInputField(
            "campaign ID",
            "campaignId",
            "Enter campaign ID",
            "The ads campaign id. One of campaign ID or campaign name is required.",
            !formData.campaignName
          )}

          {renderInputField(
            "campaign source",
            "campaignSource",
            "Enter campaign source",
            "The referrer (e.g. google, newsletter)",
            true,
            ["google", "newsletter"]
          )}

          {renderInputField(
            "campaign medium",
            "campaignMedium",
            "Enter campaign medium",
            "Marketing medium (e.g. cpc, banner, email)",
            true,
            ["cpc", "banner", "email"]
          )}

          {renderInputField(
            "campaign name",
            "campaignName",
            "Enter campaign name",
            "Product, promo code, or slogan (e.g. spring_sale) One of campaign name or campaign ID is required.",
            !formData.campaignId,
            ["spring_sale"]
          )}

          {renderInputField(
            "campaign term",
            "campaignTerm",
            "Enter campaign term",
            "Identify the paid keywords",
            false
          )}

          {renderInputField(
            "campaign content",
            "campaignContent",
            "Enter campaign content",
            "Use to differentiate ads",
            false
          )}
        </Card>

        <Card className="utm-parameters__result-card">
          {generatedUrl ? (
            <>
              <div className="utm-parameters__result-header">
                <Text className="utm-parameters__result-title">
                  GENERATED URL
                </Text>
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={handleCopyUrl}
                  className="utm-parameters__copy-btn"
                >
                  Copy URL
                </Button>
              </div>
              <div className="utm-parameters__url-box">
                <Text className="utm-parameters__url-text">{generatedUrl}</Text>
              </div>
            </>
          ) : (
            <div className="utm-parameters__placeholder">
              <div className="utm-parameters__placeholder-icon">
                <InfoCircleOutlined />
              </div>
              <Text className="utm-parameters__placeholder-text">
                Fill out required fields above and a URL will be generated for
                you here.
              </Text>
            </div>
          )}
        </Card>
      </div>
    </FiltersWrapper>
  );
};

export default UtmParameters;
